import React, { useCallback, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import MobileSelectDates from '../Common/MobileSelectDates'

import ModalBase from 'LuxKit/Modal/ModalBase'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useUpdateTrip, useTrip } from 'tripPlanner/hooks/api/trip'
import * as Mapper from 'tripPlanner/mappers'

interface Props {
  tripId: string
}

/** Just like the version in EditTripDatesTP, except this one is for use with ModalContext */
function EditTripDates(props: Props) {
  const { tripId } = props

  const { data: trip } = useTrip({ tripId })

  const { startDate, endDate } = trip ?? {}

  const initialHasDates = useRef(!!(startDate && endDate)).current

  const modalElement = useModalElementContext<{
    startDate: moment.Moment | undefined
    endDate: moment.Moment | undefined
  }>()
  const intl = useIntl()

  const datesRemovedMessage = intl.formatMessage({
    id: 'E/7bl5FzoU',
    defaultMessage: 'Dates removed',
    description: 'Snackbar message after removing dates from a trip',
  })

  const datesSavedMessage = intl.formatMessage({
    id: 'OnjmcRB8Re',
    defaultMessage: 'Dates saved',
    description: 'Snackbar message after saving dates to a trip',
  })

  const datesErrorMessage = intl.formatMessage({
    id: 'B5+2O8poH1',
    defaultMessage: 'Error while updating trip dates. Please try again.',
    description: 'Error message when trip date update fails',
  })

  const editDatesTitle = intl.formatMessage({
    id: '/B3toa6Gmx',
    defaultMessage: 'Edit dates',
    description: 'Modal title when editing existing trip dates',
  })

  const addDatesTitle = intl.formatMessage({
    id: 'xbjj8+MHo8',
    defaultMessage: 'Add dates',
    description: 'Modal title when adding dates to a trip for the first time',
  })

  const saveLabel = intl.formatMessage({
    id: 'TdUbSHUafj',
    defaultMessage: 'Save',
    description: 'Save button label in the trip dates modal',
  })

  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

  const { mutate, isLoading } = useUpdateTrip({
    onMutate: (): undefined => {
      setErrorMsg(undefined)
    },
    onSuccess: (updatedTripRaw) => {
      const trip = Mapper.fullTrip(updatedTripRaw)
      modalElement.resolve({ startDate: trip.startDate, endDate: trip.endDate })
      const hasDates = !!(trip.startDate && trip.endDate)
      if (initialHasDates && !hasDates) {
        showSnackbar(datesRemovedMessage, 'success')
      } else if (hasDates) {
        showSnackbar(datesSavedMessage, 'success')
      }
    },
    onError: () => {
      setErrorMsg(datesErrorMessage)
    },
  })

  const updateTripDates = useCallback(
    (startDate: moment.Moment, endDate?: moment.Moment) => {
      mutate({
        tripId,
        startDate,
        endDate: endDate ?? startDate,
      })
    },
    [mutate, tripId],
  )

  const clearTripDates = useCallback(() => {
    mutate({
      tripId,
      startDate: null,
      endDate: null,
    })
  }, [mutate, tripId])

  return (
    <ModalBase>
      <MobileSelectDates
        initialStartDate={startDate}
        initialEndDate={endDate}
        onChange={updateTripDates}
        onRemove={startDate ? clearTripDates : undefined}
        onCancel={() =>
          modalElement.resolve({
            startDate: trip?.startDate,
            endDate: trip?.endDate,
          })}
        title={startDate ? editDatesTitle : addDatesTitle}
        doneLabel={startDate ? saveLabel : addDatesTitle}
        isSubmitting={isLoading}
        errorMsg={errorMsg}
      />
    </ModalBase>
  )
}

export default EditTripDates
